import React, { memo, useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Paper,
  Tooltip,
  Typography,
  Grid
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import { Collector, CollectorSettingScope } from 'graphql/types';
import { useMeContext } from 'modules/me';
import { useFeatureFlag } from 'modules/common/hooks';
import { isKeyPressed } from 'modules/common/hooks/isKeyPressed';
import { CollectorInstallInfo } from './AllCollectorsTab';
import CollectorStatus from './CollectorStatus';
import { CollectorIconType } from './AllCollectorsListPage';
import { SyncStatus } from './utils';
import { useCollectorSyncStatusContext } from './contexts/CollectorSyncStatusContextProvider';

type useStylesProp = {
  isInstalled: number;
  hasInstallError: boolean;
};

const useStyles = makeStyles((theme) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: theme.spacing(28),
    borderRadius: theme.spacing(1),
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(2),
    width: theme.spacing(25),
    borderColor: ({ hasInstallError }: useStylesProp) =>
      hasInstallError ? theme.palette.error.dark : 'rgba(0,0,0,0.12)',
    backgroundColor: theme.palette.grey[200],
    '&:hover': {
      boxShadow: '0 0 5px rgba(33,33,33,.3)'
    },
    '&:focus': {
      outline: 'none',
      boxShadow: '0 0 5px rgba(33,33,33,.3)'
    },
    paddingBottom: theme.spacing(1),
    cursor: 'pointer'
  },
  media: {
    borderRadius: theme.spacing(0),
    height: theme.spacing(15),
    display: 'flex',
    paddingLeft: theme.spacing(4.5),
    alignItems: 'center'
  },
  collectorIcon: {
    maxHeight: theme.spacing(10),
    flexGrow: 1
  },

  title: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightRegular,
    overflow: 'visible',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    position: 'relative'
  },
  connected: {
    padding: theme.spacing(0, 0.8),
    ...theme.typography.caption,
    backgroundColor: theme.palette.success.dark,
    color: theme.palette.getContrastText(theme.palette.success.dark),
    marginLeft: theme.spacing(1.5),
    float: 'left',
    position: 'relative',
    bottom: theme.spacing(1.2),
    top: theme.spacing(-1.2),
    width: 'fit-content',
    borderRadius: theme.spacing(0.5)
  },
  cardContent: {
    padding: ({ isInstalled }: useStylesProp) =>
      isInstalled > 0
        ? `${theme.spacing(0, 2, 0, 2)} !important`
        : `${theme.spacing(2.2, 2, 0, 2)} !important`,

    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(1),
    justifyContent: 'space-between'
  },

  category: {
    display: 'flex',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize
  },
  categoryBox: {
    position: 'relative',
    margin: theme.spacing(-2.75, 0, 1, 0)
  }
}));

const useTooltipClasses = makeStyles((theme) => ({
  popper: {
    marginBottom: `${theme.spacing(-13)} !important`
  }
}));

type CollectorCardComponentProps = {
  info: Collector;
  index: number;
  handleOpenDialog: (item: any) => void;
  settingObj: Record<string, CollectorInstallInfo>;
};

export const CollectorCardComponent: React.FC<CollectorCardComponentProps> = memo(
  ({
    info,
    index,
    handleOpenDialog,
    settingObj
  }: CollectorCardComponentProps) => {
    const me = useMeContext();
    const isAdmin = useMemo(() => {
      return me?.permissions.hasCompanyCollectorInstallAccess ? true : false;
    }, [me]);
    const { formatMessage } = useIntl();
    const altText = formatMessage(
      {
        id: 'collectors.imageAltText'
      },
      { collectorName: info.name }
    );

    const tooltip = useTooltipClasses();

    const getStatus = useCallback(
      (
        info: Collector
      ): {
        retUserStatus: string;
        retTenantStatus: string;
      } => {
        const collectorSettingDetails = settingObj[info?.id];

        const userStatus = collectorSettingDetails?.userSettings?.find(
          (s) => s?.key === 'syncStatus'
        )?.value;
        const tenantStatus = collectorSettingDetails?.tenantSettings?.find(
          (s) => s?.key === 'syncStatus'
        )?.value;
        if (userStatus || (tenantStatus && isAdmin)) {
          const retUserStatus = (userStatus || '').trim();
          const retTenantStatus = (tenantStatus || '')?.trim();
          if (retUserStatus.length > 0 || retTenantStatus?.length > 0)
            return { retUserStatus, retTenantStatus };
        }
        return {
          retUserStatus:
            (collectorSettingDetails?.userInstallStatus?.errors?.length || 0) >
            0
              ? 'Failed'
              : '',
          retTenantStatus:
            (collectorSettingDetails?.tenantInstallStatus?.errors?.length ||
              0) > 0 && isAdmin
              ? 'Failed'
              : ''
        };
      },
      [isAdmin, settingObj]
    );

    const getInstallId = useCallback((item: Collector) => {
      let userInstallId = '';
      let installId = '';
      item.installations?.items?.forEach((i) => {
        if (i?.scope === CollectorSettingScope.User)
          userInstallId = i?.installId;
        else installId = i?.installId || '';
      });
      return { userInstallId, installId };
    }, []);

    const { userInstallId, installId } = getInstallId(info);
    const { collectorsSyncStatus } = useCollectorSyncStatusContext();

    const hasInstallError = useMemo(() => {
      if (
        collectorsSyncStatus[installId] === SyncStatus.failed ||
        collectorsSyncStatus[installId] === SyncStatus.partiallyCompleted ||
        collectorsSyncStatus[userInstallId as string] === SyncStatus.failed ||
        collectorsSyncStatus[userInstallId as string] ===
          SyncStatus.partiallyCompleted
      ) {
        return true;
      } else {
        return false;
      }
    }, [collectorsSyncStatus, installId, userInstallId]);

    const classes = useStyles({
      isInstalled: info.installations?.items?.length || 0,
      hasInstallError
    });

    const getInstallStatus = useCallback(
      (item: Collector) => {
        const { retUserStatus, retTenantStatus } = getStatus(item);
        if ((item?.installations?.items?.length || 0) > 0)
          return (
            <div>
              <span
                className={classes.connected}
                data-testid={'connected-button-' + item.name}
              >
                <FormattedMessage id="collectors.installed" />
              </span>
              {(retTenantStatus || retUserStatus) &&
                (item?.installations?.items?.length || 0) && (
                  <CollectorStatus
                    onClick={() => handleOpenDialog(item)}
                    syncStatus={retTenantStatus}
                    installId={installId}
                    userSyncStatus={retUserStatus}
                    userInstallId={userInstallId}
                    hasInstallError={hasInstallError}
                  />
                )}
            </div>
          );
      },
      [
        classes.connected,
        getStatus,
        handleOpenDialog,
        hasInstallError,
        installId,
        userInstallId
      ]
    );

    const isTiwVpatEnabled = useFeatureFlag('isTiwVpatEnabled');
    const handleKeyDown = useCallback(
      (event) => {
        const isEnterKeyPressed = isKeyPressed(event.key, 'Enter');

        if (isEnterKeyPressed) {
          handleOpenDialog(info);
        }
      },
      [handleOpenDialog, info]
    );

    return (
      <Grid item key={info.id} data-testid={`collectoritem${info.name}`}>
        <Card
          className={classes.card}
          elevation={0}
          variant="outlined"
          key={info.id}
          onClick={() => handleOpenDialog(info)}
          data-testid={`collectorcard${info.name}`}
          tabIndex={isTiwVpatEnabled ? 0 : -1}
          onKeyDown={handleKeyDown}
        >
          <CardMedia>
            <Paper className={classes.media} elevation={0}>
              <img
                className={classes.collectorIcon}
                src={info.image.src}
                alt={altText}
                data-testid={`collectorImage${index}`}
              />
              <CollectorIconType type={info.type} />
            </Paper>
          </CardMedia>
          {getInstallStatus(info)}
          <CardContent className={classes.cardContent}>
            <Tooltip
              title={info.name}
              data-testid={`collector-title-tooltip${index}`}
              placement="top-start"
              classes={tooltip}
            >
              <Typography
                className={classes.title}
                variant="body1"
                data-testid={`collectorName${index}`}
              >
                {info.name}
              </Typography>
            </Tooltip>
            <div
              data-testid={`collector-category-content${index}`}
              className={classes.categoryBox}
            >
              <Typography className={classes.category}>
                {info.category}
              </Typography>
            </div>
          </CardContent>
        </Card>
      </Grid>
    );
  }
);
CollectorCardComponent.displayName = 'CollectorCardComponent';
